import { entity } from "simpler-state"
import { run_accounting_wizard_stream, type AccountingWizardOutcome } from "@/api/accounting_wizard"
import { run_split_wizard_stream, type SplitWizOutcome } from "@/api/split_wizard"
import type { CodingWizRunMode } from "./coding_wiz_preferences"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"
import { toast } from "@/components/ui/use-toast.ts"
import { ToastAction } from "@/components/ui/toast"

/**
 * Bulk CodingWiz runs live here, not in the sheet that shows their results.
 *
 * The old flow posted every selected transaction to `/accounting/wizard/bulk` and waited on the
 * single response, so for the minutes it took the user saw one "Running…" toast (and only until
 * the next toast replaced it). Now each transaction streams through the same SSE endpoints the
 * single-run sheet uses, a few at a time, and every progress line and outcome lands in this store
 * as it happens. The check detail table reads it to show a spinner and then a result on each
 * row, and the floating progress panel reads it for the overall count.
 */

export type BulkCodingWizOutcome = AccountingWizardOutcome | SplitWizOutcome

export type BulkCodingWizRowState =
  | { status: "queued" }
  | { status: "running"; message: string | null }
  | { status: "done"; outcome: BulkCodingWizOutcome }
  | { status: "error"; error: string }

export interface BulkCodingWizRun {
  run_id: number
  mode: CodingWizRunMode
  is_accounting: boolean
  /** Selection order — the order the results sheet lists them in. */
  deduction_ids: string[]
  /** Invoice number per deduction id, captured from the table rows at start, for display. */
  labels: Record<string, string>
  rows: Record<string, BulkCodingWizRowState>
  started_at: string
  finished_at: string | null
  /** The progress panel was collapsed to a pill by the user. */
  minimized: boolean
}

export interface BulkCodingWizCounts {
  total: number
  queued: number
  running: number
  succeeded: number
  failed: number
}

/** The latest run; stays around after finishing so results can still be opened. */
export const BulkCodingWizState = entity<BulkCodingWizRun | null>(null)
/** Whether the results sheet is open. */
export const BulkCodingWizResultsOpen = entity(false)

/** How many transactions stream at once. Browsers cap open connections per host at about six. */
const DEFAULT_CONCURRENCY = 4
/** Coalesce table refreshes so ten outcomes in a second do not refetch the check ten times. */
const REFRESH_THROTTLE_MS = 1500

let run_counter = 0
let refresh_timer: ReturnType<typeof setTimeout> | null = null

/** A finished row that did not code anything counts as a failure, whichever way it reported it. */
export function bulk_coding_wiz_row_failed(row: BulkCodingWizRowState | undefined): boolean {
  if (!row) return false
  if (row.status === "error") return true
  return row.status === "done" && !row.outcome.success
}

export function bulk_coding_wiz_counts(run: BulkCodingWizRun | null): BulkCodingWizCounts {
  const counts: BulkCodingWizCounts = { total: 0, queued: 0, running: 0, succeeded: 0, failed: 0 }
  if (!run) return counts
  for (const id of run.deduction_ids) {
    counts.total += 1
    const row = run.rows[id]
    if (!row || row.status === "queued") {
      counts.queued += 1
    } else if (row.status === "running") {
      counts.running += 1
    } else if (bulk_coding_wiz_row_failed(row)) {
      counts.failed += 1
    } else {
      counts.succeeded += 1
    }
  }
  return counts
}

export function bulk_coding_wiz_is_finished(counts: BulkCodingWizCounts): boolean {
  return counts.total > 0 && counts.queued + counts.running === 0
}

/** "3 of 10 done" while running; "8 coded, 2 failed" once finished. */
export function bulk_coding_wiz_summary(counts: BulkCodingWizCounts): string {
  if (!bulk_coding_wiz_is_finished(counts)) {
    return `${counts.succeeded + counts.failed} of ${counts.total} done`
  }
  const parts = [`${counts.succeeded} coded`]
  if (counts.failed > 0) {
    parts.push(`${counts.failed} failed`)
  }
  return parts.join(", ")
}

/**
 * Run `worker` over `items` with at most `limit` in flight, in order of submission. A worker that
 * throws does not stop the others; callers record their own failures.
 */
export async function run_with_concurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>
): Promise<void> {
  let next = 0
  async function lane(): Promise<void> {
    while (next < items.length) {
      const item = items[next]
      next += 1
      try {
        await worker(item)
      } catch {
        // The worker owns error reporting; the lane keeps going.
      }
    }
  }
  const lane_count = Math.max(1, Math.min(limit, items.length))
  await Promise.all(Array.from({ length: lane_count }, lane))
}

function set_row(run_id: number, deduction_id: string, state: BulkCodingWizRowState) {
  BulkCodingWizState.set(run =>
    run && run.run_id === run_id ? { ...run, rows: { ...run.rows, [deduction_id]: state } } : run
  )
}

function schedule_refresh() {
  if (refresh_timer) return
  refresh_timer = setTimeout(() => {
    refresh_timer = null
    triggerRefreshFlag()
  }, REFRESH_THROTTLE_MS)
}

function refresh_now() {
  if (refresh_timer) {
    clearTimeout(refresh_timer)
    refresh_timer = null
  }
  triggerRefreshFlag()
}

export interface StartBulkCodingWizOptions {
  deduction_ids: string[]
  mode: CodingWizRunMode
  feedback: string | null
  is_accounting: boolean
  /** Invoice number per deduction id, so progress and results can name rows. */
  labels?: Record<string, string>
  concurrency?: number
}

async function run_one(run_id: number, deduction_id: string, options: StartBulkCodingWizOptions) {
  set_row(run_id, deduction_id, { status: "running", message: null })
  const stream_options = { feedback: options.feedback ?? undefined }
  function on_progress(message: string) {
    set_row(run_id, deduction_id, { status: "running", message })
  }
  function on_outcome(outcome: BulkCodingWizOutcome) {
    set_row(run_id, deduction_id, { status: "done", outcome })
    schedule_refresh()
  }
  function on_error(error: string) {
    set_row(run_id, deduction_id, { status: "error", error })
  }
  try {
    if (options.mode === "ledger") {
      await run_accounting_wizard_stream(deduction_id, { on_progress, on_outcome, on_error }, stream_options)
    } else {
      await run_split_wizard_stream(deduction_id, { on_progress, on_outcome, on_error }, stream_options)
    }
  } catch (err) {
    set_row(run_id, deduction_id, {
      status: "error",
      error: err instanceof Error ? err.message : "Request failed",
    })
  }
  // The stream can close without ever sending an outcome (proxy timeout, dropped connection).
  const row = BulkCodingWizState.get()?.rows[deduction_id]
  if (row?.status === "running") {
    set_row(run_id, deduction_id, { status: "error", error: "The connection closed before CodingWiz finished." })
  }
}

async function run_all(run_id: number, options: StartBulkCodingWizOptions) {
  await run_with_concurrency(options.deduction_ids, options.concurrency ?? DEFAULT_CONCURRENCY, id =>
    run_one(run_id, id, options)
  )
  BulkCodingWizState.set(run =>
    run && run.run_id === run_id ? { ...run, finished_at: new Date().toISOString() } : run
  )
  refresh_now()
  const run = BulkCodingWizState.get()
  if (!run || run.run_id !== run_id) return
  const counts = bulk_coding_wiz_counts(run)
  toast({
    title: "CodingWiz complete",
    description: bulk_coding_wiz_summary(counts),
    variant: counts.succeeded === 0 && counts.failed > 0 ? "destructive" : "default",
    action: (
      <ToastAction altText="View results" onClick={() => BulkCodingWizResultsOpen.set(true)}>
        View results
      </ToastAction>
    ),
  })
}

/**
 * Kick off a bulk run in the background. Returns false (with a toast) when one is still running —
 * a second run would double up on the same transactions and muddle the per-row status.
 */
export function start_bulk_coding_wiz(options: StartBulkCodingWizOptions): boolean {
  if (options.deduction_ids.length === 0) {
    return false
  }
  const current = BulkCodingWizState.get()
  if (current && !bulk_coding_wiz_is_finished(bulk_coding_wiz_counts(current))) {
    toast({
      variant: "destructive",
      title: "CodingWiz is already running",
      description: `Wait for the current run on ${current.deduction_ids.length} transaction${
        current.deduction_ids.length === 1 ? "" : "s"
      } to finish.`,
    })
    return false
  }
  run_counter += 1
  const run_id = run_counter
  const rows: Record<string, BulkCodingWizRowState> = {}
  for (const id of options.deduction_ids) {
    rows[id] = { status: "queued" }
  }
  BulkCodingWizState.set({
    run_id,
    mode: options.mode,
    is_accounting: options.is_accounting,
    deduction_ids: [...options.deduction_ids],
    labels: options.labels ?? {},
    rows,
    started_at: new Date().toISOString(),
    finished_at: null,
    minimized: false,
  })
  BulkCodingWizResultsOpen.set(false)
  void run_all(run_id, options)
  return true
}

/** Forget the last run (only meaningful once it has finished). */
export function clear_bulk_coding_wiz() {
  BulkCodingWizState.set(null)
  BulkCodingWizResultsOpen.set(false)
}

export function set_bulk_coding_wiz_minimized(minimized: boolean) {
  BulkCodingWizState.set(run => (run ? { ...run, minimized } : run))
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function make_run(rows: Record<string, BulkCodingWizRowState>): BulkCodingWizRun {
    return {
      run_id: 1,
      mode: "ledger",
      is_accounting: true,
      deduction_ids: Object.keys(rows),
      labels: {},
      rows,
      started_at: "2026-09-10T00:00:00Z",
      finished_at: null,
      minimized: false,
    }
  }

  function outcome(success: boolean): AccountingWizardOutcome {
    return {
      deduction_id: "d",
      path: success ? "gemini_full_list" : "error",
      summary_bullets: [],
      summary_markdown: "",
      success,
    }
  }

  describe("bulk_coding_wiz_counts", () => {
    it("buckets rows by status and treats an unsuccessful outcome as a failure", () => {
      const counts = bulk_coding_wiz_counts(
        make_run({
          a: { status: "queued" },
          b: { status: "running", message: "Loading backup" },
          c: { status: "done", outcome: outcome(true) },
          d: { status: "done", outcome: outcome(false) },
          e: { status: "error", error: "boom" },
        })
      )
      expect(counts).toEqual({ total: 5, queued: 1, running: 1, succeeded: 1, failed: 2 })
      expect(bulk_coding_wiz_is_finished(counts)).toBe(false)
      expect(bulk_coding_wiz_summary(counts)).toBe("3 of 5 done")
    })

    it("summarises a finished run by coded and failed", () => {
      const counts = bulk_coding_wiz_counts(
        make_run({ a: { status: "done", outcome: outcome(true) }, b: { status: "error", error: "x" } })
      )
      expect(bulk_coding_wiz_is_finished(counts)).toBe(true)
      expect(bulk_coding_wiz_summary(counts)).toBe("1 coded, 1 failed")
      expect(
        bulk_coding_wiz_summary(bulk_coding_wiz_counts(make_run({ a: { status: "done", outcome: outcome(true) } })))
      ).toBe("1 coded")
    })

    it("is empty and unfinished with no run", () => {
      const counts = bulk_coding_wiz_counts(null)
      expect(counts.total).toBe(0)
      expect(bulk_coding_wiz_is_finished(counts)).toBe(false)
    })
  })

  describe("run_with_concurrency", () => {
    it("never has more than the limit in flight and still runs every item", async () => {
      let in_flight = 0
      let peak = 0
      const seen: number[] = []
      await run_with_concurrency([1, 2, 3, 4, 5, 6, 7], 3, async (item: number) => {
        in_flight += 1
        peak = Math.max(peak, in_flight)
        await new Promise(resolve => setTimeout(resolve, 2))
        seen.push(item)
        in_flight -= 1
      })
      expect(peak).toBe(3)
      expect(seen.sort((a: number, b: number) => a - b)).toEqual([1, 2, 3, 4, 5, 6, 7])
    })

    it("keeps going when a worker throws", async () => {
      const seen: number[] = []
      await run_with_concurrency([1, 2, 3], 1, async (item: number) => {
        if (item === 2) throw new Error("boom")
        seen.push(item)
      })
      expect(seen).toEqual([1, 3])
    })

    it("handles an empty list", async () => {
      await expect(run_with_concurrency([], 4, async () => {})).resolves.toBeUndefined()
    })
  })
}
